/**
 * Motor de simulación del plan de producción (100% cliente).
 *
 * Modelo MRP simplificado con NETTING por nivel:
 * - La demanda del plan se explota por el BOM nivel a nivel. En cada
 *   SEMIELABORADO la demanda se sirve primero de su suministro disponible
 *   (stock + entradas + salidas de OPs que lo producen − consumo comprometido)
 *   y solo el DÉFICIT baja a sus componentes. Antes el stock de semielaborados
 *   se ignoraba y las necesidades de materia prima quedaban sobrestimadas.
 * - Disponibilidad en HOJA con saldo acumulado por semana:
 *     saldo_fin(h,W) = stock + Σ entradas + Σ salidas_OP
 *                    − Σ comprometidos (pendiente no reservado)
 *                    − Σ demanda_neta_del_plan
 * - max_producible(A,W): mayor cantidad adicional en W que no deja en negativo
 *   ninguna hoja BLOQUEANTE en W ni en semanas posteriores, contando con la
 *   absorción de los semielaborados (búsqueda binaria sobre un test de
 *   factibilidad monótono).
 *
 * El recálculo es incremental por COMPONENTES ACOPLADOS: artículos que
 * comparten algún semielaborado se recalculan juntos; el resto no se toca.
 * Las filas del plan son inmutables (diffing por referencia).
 */
import type { PlanArticulo, PlanFeed, PlanLineaInicial, PlanMaterial, PlanQtySemana } from '@/types'

export type ItemKey = string // `${itemid}|${color}|${estilo}`

export const keyOf = (itemid: string, color: string, estilo: string): ItemKey =>
  `${itemid}|${(color || '').toUpperCase()}|${(estilo || '').toUpperCase()}`

export const keyOfArticulo = (a: { itemid: string; color: string; estilo: string }): ItemKey =>
  keyOf(a.itemid, a.color, a.estilo)

/** plan[articuloKey][weekIdx] = unidades a fabricar (filas inmutables) */
export type PlanMatrix = Record<ItemKey, Record<number, number>>

export interface LeafReq {
  leafKey: ItemKey
  reqUnit: number // unidades de hoja por unidad de artículo terminado (bruto)
}

export interface SubtreeMaterial {
  key: ItemKey
  reqUnit: number // necesidad bruta por unidad de artículo
  isSemi: boolean
}

export interface ShortageItem {
  leafKey: ItemKey
  itemid: string
  color: string
  estilo: string
  descripcion: string
  firstWeekIdx: number   // primera semana con saldo negativo
  maxDeficit: number     // mayor déficit (valor positivo)
  nextInflowIdx: number | null // primera entrada de compra ≥ firstWeekIdx
}

export interface PlanEngineOptions {
  /** false → las compras con fecha vencida NO cuentan como suministro. */
  trustOverduePurchases?: boolean
  /** true → planificar desde 0: ignora las OPs en curso por completo
   *  (ni consumo comprometido, ni producción futura, ni filas "En curso"). */
  ignoreInProgress?: boolean
}

interface BomChild {
  compKey: ItemKey
  compItem: string
  qty: number
}

interface NodeInfo {
  key: ItemKey
  children: BomChild[] | null // null → hoja
}

const EPS = 1e-9

export class PlanEngine {
  readonly numWeeks: number
  readonly nowIdx: number
  readonly materialByKey = new Map<ItemKey, PlanMaterial>()
  readonly warnings: string[] = []

  private readonly anioHorizonte: number
  private readonly trustOverdue: boolean
  private readonly ignoreInProgress: boolean
  private readonly weekIdx = new Map<string, number>()
  private readonly etiquetaIdx = new Map<string, number>()

  // ── BOM / grafo ─────────────────────────────────────────────────────────────
  private readonly parentItemIds = new Set<string>()
  private readonly childrenByParentKey = new Map<ItemKey, BomChild[]>()
  private readonly parentKeysByItem = new Map<string, ItemKey[]>()
  private readonly nodeInfo = new Map<ItemKey, NodeInfo>()
  private readonly articleSet = new Set<ItemKey>()
  private readonly semiSet = new Set<ItemKey>()
  private readonly leafSet = new Set<ItemKey>()

  // ── Por artículo ────────────────────────────────────────────────────────────
  private readonly leafReqByArticle = new Map<ItemKey, LeafReq[]>()
  private readonly subtreeByArticle = new Map<ItemKey, SubtreeMaterial[]>()
  private readonly articleTopo = new Map<ItemKey, ItemKey[]>() // artículo + semis, topo
  private readonly articlesByLeaf = new Map<ItemKey, { articleKey: ItemKey; reqUnit: number }[]>()

  // ── Componentes acoplados (artículos que comparten semielaborados) ─────────
  private readonly compOfArticle = new Map<ItemKey, number>()
  private readonly compTopo = new Map<number, ItemKey[]>() // artículos + semis, topo
  private readonly compLeafContrib = new Map<number, Map<ItemKey, Float64Array>>()
  private numComps = 0

  // ── Suministro estático por material (hoja o semi) ─────────────────────────
  private readonly inflow = new Map<ItemKey, Float64Array>()    // entradas + salidas OP
  private readonly committed = new Map<ItemKey, Float64Array>() // pendiente no reservado
  private readonly baseStock = new Map<ItemKey, number>()

  // ── Estado dinámico ─────────────────────────────────────────────────────────
  private readonly leafDemand = new Map<ItemKey, Float64Array>() // demanda neta del plan
  private readonly saldoFin = new Map<ItemKey, Float64Array>()   // hojas Y semis
  private readonly semiShort = new Map<ItemKey, Float64Array>()  // déficit que baja a hijos
  private lastPlan: PlanMatrix = {}
  private pendingInitial = true

  // ── Caches (se invalidan en cada compute / override) ───────────────────────
  private readonly suffixMinCache = new Map<ItemKey, Float64Array>()
  private readonly maxCache = new Map<string, { max: number; limitKey: ItemKey | null }>()
  private blockingOverrides = new Map<ItemKey, boolean>()

  // ── Series informativas por artículo (solo display) ────────────────────────
  private readonly baselineByArticle = new Map<ItemKey, Float64Array>()
  private readonly demandaByArticle = new Map<ItemKey, Float64Array>()
  private readonly realizadoByArticle = new Map<ItemKey, Float64Array>()

  constructor(feed: PlanFeed, options: PlanEngineOptions = {}) {
    this.numWeeks = feed.horizonte.length
    this.anioHorizonte = feed.anio
    this.trustOverdue = options.trustOverduePurchases !== false
    this.ignoreInProgress = options.ignoreInProgress === true
    feed.horizonte.forEach((s, i) => {
      this.weekIdx.set(`${s.anio}-${s.semana}`, i)
      this.etiquetaIdx.set(s.etiqueta, i)
    })

    const iso = new Date().toISOString().slice(0, 10)
    const nowI = feed.horizonte.findIndex((s) => s.fechaInicio <= iso && iso <= s.fechaFin)
    this.nowIdx = nowI >= 0 ? nowI : iso < (feed.horizonte[0]?.fechaInicio ?? '') ? 0 : this.numWeeks

    for (const m of feed.materiales) {
      this.materialByKey.set(keyOf(m.itemid, m.color, m.estilo), m)
    }

    // Índice del BOM por padre + set de itemids fabricados
    for (const r of feed.bom) {
      this.parentItemIds.add(r.productoPadreId)
      const pKey = keyOf(r.productoPadreId, r.productoPadreColor, r.productoPadreEstilo)
      let children = this.childrenByParentKey.get(pKey)
      if (!children) {
        children = []
        this.childrenByParentKey.set(pKey, children)
        const variants = this.parentKeysByItem.get(r.productoPadreId)
        if (variants) variants.push(pKey)
        else this.parentKeysByItem.set(r.productoPadreId, [pKey])
      }
      children.push({
        compKey: keyOf(r.componenteId, r.componenteColor, r.componenteEstilo),
        compItem: r.componenteId,
        qty: r.consumoUnitario,
      })
    }

    // Subárbol, orden topológico y requerimientos brutos de cada artículo
    for (const a of feed.articulosPlanificables) {
      this.buildArticle(keyOfArticulo(a), a.itemid)
    }

    // Componentes acoplados: artículos que comparten algún semielaborado
    this.buildComponents()

    // Suministro estático de todos los materiales del grafo
    for (const key of this.semiSet) this.ensureNode(key)
    for (const key of this.leafSet) this.ensureNode(key)

    // Series informativas
    const loadSeries = (rows: PlanLineaInicial[] | undefined, target: Map<ItemKey, Float64Array>) => {
      for (const row of rows ?? []) {
        const aKey = keyOf(row.productoPadreId, row.color || '', row.estilo || '')
        let arr = target.get(aKey)
        for (const [etiqueta, qty] of Object.entries(row.cantidades)) {
          const idx = this.etiquetaIdx.get(etiqueta)
          if (idx === undefined || !(qty > 0)) continue
          if (!arr) {
            arr = new Float64Array(this.numWeeks)
            target.set(aKey, arr)
          }
          arr[idx] += qty
        }
      }
    }
    // Baseline = OPs en curso. SOLO informativa: su consumo ya está en
    // `comprometidos`, así que no se suma a la demanda (se duplicaría).
    // En modo "desde 0" las OPs en curso no existen para el cálculo ni la UI.
    if (!this.ignoreInProgress) loadSeries(feed.planInicial, this.baselineByArticle)
    loadSeries(feed.demanda, this.demandaByArticle)
    loadSeries(feed.realizado, this.realizadoByArticle)

    // Primer cálculo: inicializa el netting (saldos de semis) con plan vacío.
    this.compute({})
  }

  // ── Construcción del grafo ──────────────────────────────────────────────────

  private resolveParentKey(key: ItemKey, itemid: string): ItemKey | null {
    if (this.childrenByParentKey.has(key)) return key
    const blank = keyOf(itemid, '', '')
    if (this.childrenByParentKey.has(blank)) return blank
    const variants = this.parentKeysByItem.get(itemid)
    return variants && variants.length > 0 ? variants[0] : null
  }

  private ensureNodeInfo(key: ItemKey, itemid: string): NodeInfo {
    let info = this.nodeInfo.get(key)
    if (info) return info
    let children: BomChild[] | null = null
    if (this.parentItemIds.has(itemid)) {
      const parentKey = this.resolveParentKey(key, itemid)
      if (parentKey) children = this.childrenByParentKey.get(parentKey)!
      // Fabricado sin BOM resoluble para esta variante → se trata como hoja
    }
    info = { key, children }
    this.nodeInfo.set(key, info)
    return info
  }

  /** DFS del subárbol del artículo: nodeInfo, orden topo, req brutos, hojas. */
  private buildArticle(aKey: ItemKey, itemid: string): void {
    this.articleSet.add(aKey)
    this.ensureNodeInfo(aKey, itemid)

    const postOrder: ItemKey[] = []
    const inStack = new Set<string>()
    const visited = new Set<ItemKey>()

    const dfs = (key: ItemKey, kItem: string): void => {
      if (visited.has(key)) return
      if (inStack.has(kItem)) {
        this.warnings.push(`Ciclo en BOM detectado en ${kItem} — rama cortada`)
        return
      }
      visited.add(key)
      const info = this.ensureNodeInfo(key, kItem)
      if (info.children) {
        inStack.add(kItem)
        for (const c of info.children) dfs(c.compKey, c.compItem)
        inStack.delete(kItem)
        if (key !== aKey) this.semiSet.add(key)
      } else if (key !== aKey) {
        this.leafSet.add(key)
      }
      postOrder.push(key)
    }
    dfs(aKey, itemid)

    // Reverse post-order = orden topológico (padres antes que componentes)
    const topoAll = postOrder.reverse()
    const topo = topoAll.filter((k) => k === aKey || this.nodeInfo.get(k)!.children)
    this.articleTopo.set(aKey, topo)

    // Requerimiento bruto por unidad (acumula diamantes en orden topo)
    const gross = new Map<ItemKey, number>([[aKey, 1]])
    for (const key of topo) {
      const g = gross.get(key) ?? 0
      if (g <= 0) continue
      for (const c of this.nodeInfo.get(key)!.children ?? []) {
        gross.set(c.compKey, (gross.get(c.compKey) ?? 0) + g * c.qty)
      }
    }

    const subtree: SubtreeMaterial[] = []
    for (const key of topoAll) {
      if (key === aKey) continue
      const g = gross.get(key) ?? 0
      if (g <= 0) continue
      subtree.push({ key, reqUnit: g, isSemi: !!this.nodeInfo.get(key)!.children })
    }
    // Semis primero (en orden topo), hojas después ordenadas por clave
    subtree.sort((x, y) =>
      x.isSemi !== y.isSemi ? (x.isSemi ? -1 : 1) : x.key.localeCompare(y.key),
    )
    this.subtreeByArticle.set(aKey, subtree)

    const leaves: LeafReq[] = subtree
      .filter((s) => !s.isSemi)
      .map((s) => ({ leafKey: s.key, reqUnit: s.reqUnit }))
    this.leafReqByArticle.set(aKey, leaves)
    for (const l of leaves) {
      const arr = this.articlesByLeaf.get(l.leafKey)
      if (arr) arr.push({ articleKey: aKey, reqUnit: l.reqUnit })
      else this.articlesByLeaf.set(l.leafKey, [{ articleKey: aKey, reqUnit: l.reqUnit }])
    }
  }

  /** Union-find de artículos vía semielaborados compartidos + topo por componente. */
  private buildComponents(): void {
    const articles = [...this.articleSet]
    const parent = articles.map((_, i) => i)
    const find = (i: number): number => {
      while (parent[i] !== i) {
        parent[i] = parent[parent[i]]
        i = parent[i]
      }
      return i
    }
    const union = (a: number, b: number) => {
      const ra = find(a)
      const rb = find(b)
      if (ra !== rb) parent[rb] = ra
    }

    const idxOfArticle = new Map<ItemKey, number>()
    articles.forEach((a, i) => idxOfArticle.set(a, i))
    const articlesBySemi = new Map<ItemKey, number[]>()
    for (const aKey of articles) {
      for (const s of this.subtreeByArticle.get(aKey) ?? []) {
        if (!s.isSemi) continue
        const arr = articlesBySemi.get(s.key)
        if (arr) arr.push(idxOfArticle.get(aKey)!)
        else articlesBySemi.set(s.key, [idxOfArticle.get(aKey)!])
      }
    }
    for (const idxs of articlesBySemi.values()) {
      for (let i = 1; i < idxs.length; i++) union(idxs[0], idxs[i])
    }

    const compIdOfRoot = new Map<number, number>()
    const compArticles = new Map<number, ItemKey[]>()
    for (let i = 0; i < articles.length; i++) {
      const root = find(i)
      let compId = compIdOfRoot.get(root)
      if (compId === undefined) {
        compId = this.numComps++
        compIdOfRoot.set(root, compId)
        compArticles.set(compId, [])
      }
      compArticles.get(compId)!.push(articles[i])
      this.compOfArticle.set(articles[i], compId)
    }

    // Orden topológico (artículos + semis) por componente: reverse post-order
    for (const [compId, arts] of compArticles) {
      const postOrder: ItemKey[] = []
      const visited = new Set<ItemKey>()
      const dfs = (key: ItemKey): void => {
        if (visited.has(key)) return
        visited.add(key)
        for (const c of this.nodeInfo.get(key)?.children ?? []) dfs(c.compKey)
        postOrder.push(key)
      }
      for (const aKey of arts) dfs(aKey)
      const topo = postOrder
        .reverse()
        .filter((k) => this.articleSet.has(k) || this.semiSet.has(k))
      this.compTopo.set(compId, topo)
    }
  }

  // ── Buckets semanales ──────────────────────────────────────────────────────

  /** idx de semana, -1 = anterior al horizonte (suma al saldo inicial), -2 = posterior (se ignora) */
  private resolveWeek(anio: number, semana: number): number {
    const idx = this.weekIdx.get(`${anio}-${semana}`)
    if (idx !== undefined) return idx
    if (anio < this.anioHorizonte) return -1
    if (anio > this.anioHorizonte) return -2
    return semana < 1 ? -1 : -2
  }

  private ensureNode(key: ItemKey): void {
    if (this.inflow.has(key)) return
    const n = this.numWeeks
    const inflow = new Float64Array(n)
    const committed = new Float64Array(n)
    let base = 0

    const mat = this.materialByKey.get(key)
    if (mat) {
      base = mat.stock.total
      const addBuckets = (
        buckets: PlanQtySemana[],
        target: Float64Array,
        sign: 1 | -1,
        skipOverdue: boolean,
      ) => {
        for (const b of buckets) {
          const w = this.resolveWeek(b.anio, b.semana)
          if (w === -2) continue
          if (skipOverdue && (w === -1 || w < this.nowIdx)) continue
          if (w === -1) base += sign * b.qty
          else target[w] += b.qty
        }
      }
      // Compras pendientes; si no se confía en las vencidas, no suman
      addBuckets(mat.entradas, inflow, 1, !this.trustOverdue)
      if (!this.ignoreInProgress) {
        // Output de OPs en curso que producen este material (hoja o semi)
        addBuckets(mat.salidas, inflow, 1, false)
        addBuckets(mat.comprometidos, committed, -1, false)
      }
    }

    this.inflow.set(key, inflow)
    this.committed.set(key, committed)
    this.baseStock.set(key, base)
    this.saldoFin.set(key, new Float64Array(n))
    if (this.leafSet.has(key)) {
      this.leafDemand.set(key, new Float64Array(n))
      this.recomputeLeafSaldo(key) // saldo correcto aunque nunca reciba demanda
    }
  }

  // ── Recálculo (incremental por componente acoplado) ────────────────────────

  /**
   * Aplica un plan nuevo. Recalcula solo los componentes acoplados que
   * contienen artículos cuya fila cambió de referencia.
   */
  compute(plan: PlanMatrix): void {
    const dirtyComps = new Set<number>()
    if (this.pendingInitial) {
      this.pendingInitial = false
      for (let c = 0; c < this.numComps; c++) dirtyComps.add(c)
    }
    const articleKeys = new Set([...Object.keys(plan), ...Object.keys(this.lastPlan)])
    for (const aKey of articleKeys) {
      if (plan[aKey] !== this.lastPlan[aKey]) {
        const comp = this.compOfArticle.get(aKey)
        if (comp !== undefined) dirtyComps.add(comp)
      }
    }
    this.lastPlan = plan
    if (dirtyComps.size === 0) return

    for (const compId of dirtyComps) this.recomputeComponent(compId, plan)
    this.suffixMinCache.clear()
    this.maxCache.clear()
  }

  private recomputeComponent(compId: number, plan: PlanMatrix): void {
    const n = this.numWeeks
    const touchedLeaves = new Set<ItemKey>()

    // 1. Retirar la contribución anterior del componente a las hojas
    const old = this.compLeafContrib.get(compId)
    if (old) {
      for (const [lk, vec] of old) {
        const tot = this.leafDemand.get(lk)
        if (tot) for (let w = 0; w < n; w++) tot[w] -= vec[w]
        touchedLeaves.add(lk)
      }
    }

    // 2. Propagar demanda en orden topo, con netting en cada semi
    const contrib = new Map<ItemKey, Float64Array>()
    const semiDemand = new Map<ItemKey, Float64Array>()

    for (const key of this.compTopo.get(compId) ?? []) {
      let short: Float64Array | null
      if (this.articleSet.has(key)) {
        const row = plan[key]
        if (!row) continue
        short = new Float64Array(n)
        for (const wStr of Object.keys(row)) {
          const w = Number(wStr)
          const qty = row[w]
          if (qty > 0 && w >= 0 && w < n) short[w] = qty
        }
      } else {
        // Semi: netting SIEMPRE (aunque sin demanda) para refrescar su saldo
        short = this.netSemi(key, semiDemand.get(key))
      }

      let any = false
      for (let w = 0; w < n; w++) if (short[w] > EPS) { any = true; break }
      if (!any) continue

      for (const c of this.nodeInfo.get(key)?.children ?? []) {
        if (this.semiSet.has(c.compKey)) {
          let d = semiDemand.get(c.compKey)
          if (!d) {
            d = new Float64Array(n)
            semiDemand.set(c.compKey, d)
          }
          for (let w = 0; w < n; w++) d[w] += short[w] * c.qty
        } else {
          let cv = contrib.get(c.compKey)
          if (!cv) {
            cv = new Float64Array(n)
            contrib.set(c.compKey, cv)
          }
          for (let w = 0; w < n; w++) cv[w] += short[w] * c.qty
        }
      }
    }

    // 3. Aplicar contribuciones nuevas y recalcular saldos de hojas tocadas
    for (const [lk, vec] of contrib) {
      this.ensureNode(lk)
      const tot = this.leafDemand.get(lk)!
      for (let w = 0; w < n; w++) tot[w] += vec[w]
      touchedLeaves.add(lk)
    }
    this.compLeafContrib.set(compId, contrib)
    for (const lk of touchedLeaves) this.recomputeLeafSaldo(lk)
  }

  /** Netting del semi: sirve (comprometidos + demanda) de su saldo; devuelve el déficit. */
  private netSemi(key: ItemKey, demand: Float64Array | undefined): Float64Array {
    this.ensureNode(key)
    const n = this.numWeeks
    const inflow = this.inflow.get(key)!
    const committed = this.committed.get(key)!
    const saldo = this.saldoFin.get(key)!
    let short = this.semiShort.get(key)
    if (!short) {
      short = new Float64Array(n)
      this.semiShort.set(key, short)
    }
    let b = this.baseStock.get(key)!
    for (let w = 0; w < n; w++) {
      b += inflow[w]
      const need = committed[w] + (demand ? demand[w] : 0)
      const avail = b > 0 ? b : 0
      const take = need < avail ? need : avail
      b -= take
      short[w] = need - take
      saldo[w] = b
    }
    return short
  }

  private recomputeLeafSaldo(leafKey: ItemKey): void {
    const n = this.numWeeks
    const inflow = this.inflow.get(leafKey)
    if (!inflow) return
    const committed = this.committed.get(leafKey)!
    const demand = this.leafDemand.get(leafKey)!
    const saldo = this.saldoFin.get(leafKey)!
    let acc = this.baseStock.get(leafKey)!
    for (let w = 0; w < n; w++) {
      acc += inflow[w] - committed[w] - demand[w]
      saldo[w] = acc
    }
  }

  // ── Lecturas para la UI ────────────────────────────────────────────────────

  /** Vector de necesidad bruta por hoja del artículo (req_unit). */
  leavesOf(articleKey: ItemKey): LeafReq[] {
    return this.leafReqByArticle.get(articleKey) ?? []
  }

  /** Materiales del subárbol del artículo (semis + hojas) con req bruto. */
  subtreeMaterialsOf(articleKey: ItemKey): SubtreeMaterial[] {
    return this.subtreeByArticle.get(articleKey) ?? []
  }

  /**
   * ¿Algún componente del subárbol del artículo casa con el texto de búsqueda
   * (por itemid o por descripción)? `q` debe venir ya en minúsculas.
   */
  componentMatches(articleKey: ItemKey, q: string): boolean {
    for (const m of this.subtreeByArticle.get(articleKey) ?? []) {
      const itemid = m.key.slice(0, m.key.indexOf('|'))
      if (itemid.toLowerCase().includes(q)) return true
      const desc = this.materialByKey.get(m.key)?.descripcion
      if (desc && desc.toLowerCase().includes(q)) return true
    }
    return false
  }

  isSemi(key: ItemKey): boolean {
    return this.semiSet.has(key)
  }

  /** Componentes directos de un semi: consumo POR UNIDAD DE SEMI (BOM clásico). */
  childrenOfSemi(key: ItemKey): { key: ItemKey; qty: number; isSemi: boolean }[] {
    return (this.nodeInfo.get(key)?.children ?? []).map((c) => ({
      key: c.compKey,
      qty: c.qty,
      isSemi: this.semiSet.has(c.compKey),
    }))
  }

  /**
   * Hijos DIRECTOS de un nodo (artículo o semi): consumo POR UNIDAD del propio
   * nodo. A diferencia de `subtreeMaterialsOf` (subárbol BOM aplanado, para el
   * netting MRP), esto devuelve SOLO el primer nivel de la BOM — para que la
   * matriz muestre cada componente bajo su padre directo real y no mezcle los
   * componentes de un semielaborado con los componentes directos del artículo.
   * Orden: semis primero (en orden topo), luego hojas por clave.
   */
  directChildrenOf(key: ItemKey): { key: ItemKey; qty: number; isSemi: boolean }[] {
    const children = this.childrenOfSemi(key)
    children.sort((x, y) =>
      x.isSemi !== y.isSemi ? (x.isSemi ? -1 : 1) : x.key.localeCompare(y.key),
    )
    return children
  }

  /** Déficit del semi en W = demanda que el netting deja pasar a sus hijos. */
  semiShortAt(key: ItemKey, w: number): number {
    return this.semiShort.get(key)?.[w] ?? 0
  }

  /** Entradas + salidas OP del material en la semana W (lo que suma al saldo). */
  inflowAt(key: ItemKey, w: number): number {
    return this.inflow.get(key)?.[w] ?? 0
  }

  /** Consumo comprometido (OPs en curso) del material en la semana W. */
  committedAt(key: ItemKey, w: number): number {
    return this.committed.get(key)?.[w] ?? 0
  }

  /** Demanda neta del plan sobre una hoja en la semana W. */
  leafDemandAt(key: ItemKey, w: number): number {
    return this.leafDemand.get(key)?.[w] ?? 0
  }

  /** Desglose de flujos de la semana W (valores brutos del feed, sin filtros). */
  weekDetail(key: ItemKey, w: number): { entradas: number; salidasOp: number; comprometidos: number } {
    const mat = this.materialByKey.get(key)
    const sum = (buckets: PlanQtySemana[]) => {
      let acc = 0
      for (const b of buckets) if (this.resolveWeek(b.anio, b.semana) === w) acc += b.qty
      return acc
    }
    return {
      entradas: mat ? sum(mat.entradas) : 0,
      salidasOp: mat ? sum(mat.salidas) : 0,
      comprometidos: mat ? sum(mat.comprometidos) : 0,
    }
  }

  /** Disp a INICIO de semana (antes de servir la demanda de esa semana). */
  dispInicio(key: ItemKey, w: number): number {
    const saldo = this.saldoFin.get(key)
    if (!saldo) return 0
    const prev = w > 0 ? saldo[w - 1] : this.baseStock.get(key) ?? 0
    const inflow = this.inflow.get(key)![w]
    // En semis los comprometidos se sirven dentro del netting (no se restan aquí)
    if (this.semiSet.has(key)) return prev + inflow
    return prev + inflow - this.committed.get(key)![w]
  }

  saldoFinAt(key: ItemKey, w: number): number {
    return this.saldoFin.get(key)?.[w] ?? 0
  }

  isBlocking(key: ItemKey): boolean {
    const override = this.blockingOverrides.get(key)
    if (override !== undefined) return override
    return this.materialByKey.get(key)?.isBlocking ?? true
  }

  /**
   * Overrides de usuario sobre qué materiales bloquean la producción
   * (selector en la rejilla). No cambian los saldos, solo la evaluación
   * de rojos / máximos producibles.
   */
  applyBlockingOverrides(overrides: Record<ItemKey, boolean>): void {
    this.blockingOverrides = new Map(Object.entries(overrides))
    this.maxCache.clear()
  }

  /** Rojo: saldo fin de semana negativo en hoja bloqueante. */
  isRed(key: ItemKey, w: number): boolean {
    return this.isBlocking(key) && this.saldoFinAt(key, w) < -EPS
  }

  /** Nº de hojas bloqueantes en negativo en la semana, sobre un conjunto de hojas. */
  redCount(leafKeys: Iterable<ItemKey>, w: number): number {
    let n = 0
    for (const k of leafKeys) if (this.isRed(k, w)) n++
    return n
  }

  // ── Máximo producible (con netting de semis) ───────────────────────────────

  private suffixMin(key: ItemKey, w: number): number {
    let arr = this.suffixMinCache.get(key)
    if (!arr) {
      const saldo = this.saldoFin.get(key)
      const n = this.numWeeks
      arr = new Float64Array(n)
      let m = Infinity
      for (let i = n - 1; i >= 0; i--) {
        const v = saldo ? saldo[i] : 0
        if (v < m) m = v
        arr[i] = m
      }
      this.suffixMinCache.set(key, arr)
    }
    return arr[w] ?? 0
  }

  /**
   * ¿Es factible AÑADIR x unidades del artículo en la semana w sin dejar en
   * negativo ninguna hoja bloqueante (en w ni después)? Los semis absorben
   * hasta el mínimo sufijo de su saldo. Devuelve la primera hoja violada.
   */
  private violatedLeaf(aKey: ItemKey, w: number, x: number): ItemKey | null {
    const extra = new Map<ItemKey, number>([[aKey, x]])
    const leafExtra = new Map<ItemKey, number>()
    for (const key of this.articleTopo.get(aKey) ?? []) {
      let e = extra.get(key) ?? 0
      if (e <= EPS) continue
      if (key !== aKey) {
        const absorb = Math.max(0, this.suffixMin(key, w))
        e = Math.max(0, e - absorb)
        if (e <= EPS) continue
      }
      for (const c of this.nodeInfo.get(key)?.children ?? []) {
        const add = e * c.qty
        if (this.semiSet.has(c.compKey)) {
          extra.set(c.compKey, (extra.get(c.compKey) ?? 0) + add)
        } else {
          leafExtra.set(c.compKey, (leafExtra.get(c.compKey) ?? 0) + add)
        }
      }
    }
    for (const [lk, d] of leafExtra) {
      if (!this.isBlocking(lk)) continue
      if (d > this.suffixMin(lk, w) + EPS) return lk
    }
    return null
  }

  /**
   * Máximo producible ADICIONAL del artículo en la semana W (sobre el plan ya
   * computado) sin dejar negativa ninguna hoja bloqueante en W ni después.
   * Devuelve Infinity si ninguna hoja bloqueante lo limita.
   */
  maxProducible(articleKey: ItemKey, w: number, plan?: PlanMatrix): number {
    return this.maxProducibleInfo(articleKey, w, plan).max
  }

  /** Como maxProducible, pero indica también el material limitante. */
  maxProducibleInfo(
    articleKey: ItemKey,
    w: number,
    plan?: PlanMatrix,
  ): { max: number; limitKey: ItemKey | null } {
    if (plan) this.compute(plan)
    const hasBlockingLeaf = this.leavesOf(articleKey).some(
      (l) => l.reqUnit > 0 && this.isBlocking(l.leafKey),
    )
    if (!hasBlockingLeaf) return { max: Infinity, limitKey: null }

    const cacheKey = `${articleKey}|${w}`
    const cached = this.maxCache.get(cacheKey)
    if (cached) return cached

    let result: { max: number; limitKey: ItemKey | null }
    if (this.violatedLeaf(articleKey, w, 1) !== null) {
      result = { max: 0, limitKey: this.violatedLeaf(articleKey, w, 1) }
    } else {
      let lo = 1
      let hi = 2
      while (hi < 1e9 && this.violatedLeaf(articleKey, w, hi) === null) {
        lo = hi
        hi *= 2
      }
      if (hi >= 1e9) {
        result = { max: Infinity, limitKey: null }
      } else {
        while (hi - lo > 1) {
          const mid = Math.floor((lo + hi) / 2)
          if (this.violatedLeaf(articleKey, w, mid) === null) lo = mid
          else hi = mid
        }
        result = { max: lo, limitKey: this.violatedLeaf(articleKey, w, hi) }
      }
    }
    this.maxCache.set(cacheKey, result)
    return result
  }

  /** Hojas con saldo negativo bloqueante en cualquier semana (resumen global). */
  totalRedLeaves(): number {
    let n = 0
    for (const leafKey of this.leafSet) {
      if (!this.isBlocking(leafKey)) continue
      const saldo = this.saldoFin.get(leafKey)
      if (!saldo) continue
      for (let w = 0; w < this.numWeeks; w++) {
        if (saldo[w] < -EPS) { n++; break }
      }
    }
    return n
  }

  /** Informe de faltantes: hojas bloqueantes en negativo, para compras. */
  shortageReport(): ShortageItem[] {
    const out: ShortageItem[] = []
    for (const leafKey of this.leafSet) {
      if (!this.isBlocking(leafKey)) continue
      const saldo = this.saldoFin.get(leafKey)
      if (!saldo) continue
      let firstWeekIdx = -1
      let maxDeficit = 0
      for (let w = 0; w < this.numWeeks; w++) {
        if (saldo[w] < -EPS) {
          if (firstWeekIdx < 0) firstWeekIdx = w
          if (-saldo[w] > maxDeficit) maxDeficit = -saldo[w]
        }
      }
      if (firstWeekIdx < 0) continue

      const mat = this.materialByKey.get(leafKey)
      const entradas = this.materialByKey.get(leafKey)?.entradas ?? []
      let nextInflowIdx: number | null = null
      for (const b of entradas) {
        const w = this.resolveWeek(b.anio, b.semana)
        if (w >= firstWeekIdx && b.qty > 0 && (nextInflowIdx === null || w < nextInflowIdx)) {
          nextInflowIdx = w
        }
      }
      const [itemid, color, estilo] = leafKey.split('|')
      out.push({
        leafKey,
        itemid,
        color,
        estilo,
        descripcion: mat?.descripcion ?? itemid,
        firstWeekIdx,
        maxDeficit,
        nextInflowIdx,
      })
    }
    out.sort((a, b) => a.firstWeekIdx - b.firstWeekIdx || b.maxDeficit - a.maxDeficit)
    return out
  }

  // ── Series por artículo (display) ──────────────────────────────────────────

  /** Unidades ya planificadas (OPs en curso) del artículo en la semana W. */
  baselineAt(articleKey: ItemKey, w: number): number {
    return this.baselineByArticle.get(articleKey)?.[w] ?? 0
  }

  hasBaseline(articleKey: ItemKey): boolean {
    return this.baselineByArticle.has(articleKey)
  }

  /** Total de unidades ya planificadas (OPs en curso) en todo el horizonte. */
  totalBaseline(): number {
    let total = 0
    for (const arr of this.baselineByArticle.values()) {
      for (let w = 0; w < this.numWeeks; w++) total += arr[w]
    }
    return total
  }

  /** Pedidos de venta pendientes del artículo en la semana W. */
  demandaAt(articleKey: ItemKey, w: number): number {
    return this.demandaByArticle.get(articleKey)?.[w] ?? 0
  }

  hasDemanda(articleKey: ItemKey): boolean {
    return this.demandaByArticle.has(articleKey)
  }

  /** ¿Hay datos de demanda en el feed? (la tabla puede no existir aún) */
  hasDemandaData(): boolean {
    return this.demandaByArticle.size > 0
  }

  totalDemanda(): number {
    let total = 0
    for (const arr of this.demandaByArticle.values()) {
      for (let w = 0; w < this.numWeeks; w++) total += arr[w]
    }
    return total
  }

  /** Demanda total (todos los artículos) en la semana W. */
  demandaTotalAt(w: number): number {
    let total = 0
    for (const arr of this.demandaByArticle.values()) total += arr[w] ?? 0
    return total
  }

  /** Producción en curso total (OPs) en la semana W. */
  baselineTotalAt(w: number): number {
    let total = 0
    for (const arr of this.baselineByArticle.values()) total += arr[w] ?? 0
    return total
  }

  /** Nº de hojas bloqueantes en negativo en la semana W (toda la rejilla). */
  redLeavesAt(w: number): number {
    let n = 0
    for (const leafKey of this.leafSet) {
      if (!this.isBlocking(leafKey)) continue
      const saldo = this.saldoFin.get(leafKey)
      if (saldo && saldo[w] < -EPS) n++
    }
    return n
  }

  /** Producción realizada (OPs notificadas/terminadas) en la semana W. */
  realizadoAt(articleKey: ItemKey, w: number): number {
    return this.realizadoByArticle.get(articleKey)?.[w] ?? 0
  }

  hasRealizado(articleKey: ItemKey): boolean {
    return this.realizadoByArticle.has(articleKey)
  }

  hasRealizadoData(): boolean {
    return this.realizadoByArticle.size > 0
  }

  // ── KPIs de producción ──────────────────────────────────────────────────────

  /** Nº de semanas distintas con al menos una hoja bloqueante en negativo. */
  weeksWithShortage(): number {
    let n = 0
    for (let w = 0; w < this.numWeeks; w++) {
      for (const leafKey of this.leafSet) {
        if (!this.isBlocking(leafKey)) continue
        const saldo = this.saldoFin.get(leafKey)
        if (saldo && saldo[w] < -EPS) { n++; break }
      }
    }
    return n
  }

  /**
   * Cobertura de material (%): de las hojas bloqueantes con demanda del plan,
   * porcentaje que NUNCA queda en negativo. 100 % si el plan no toca materiales.
   */
  coveragePct(): number {
    let withDemand = 0
    let covered = 0
    for (const [leafKey, demand] of this.leafDemand) {
      if (!this.isBlocking(leafKey)) continue
      let used = false
      for (let w = 0; w < this.numWeeks; w++) if (demand[w] > 0) { used = true; break }
      if (!used) continue
      withDemand++
      const saldo = this.saldoFin.get(leafKey)!
      let ok = true
      for (let w = 0; w < this.numWeeks; w++) if (saldo[w] < -EPS) { ok = false; break }
      if (ok) covered++
    }
    if (withDemand === 0) return 100
    return Math.round((covered / withDemand) * 100)
  }

  // ── Aviso de conflicto por material compartido ──────────────────────────────

  /**
   * Tras editar `editedKey`, devuelve los OTROS artículos cuya capacidad se ve
   * comprometida: comparten una hoja bloqueante con el editado y esa hoja queda
   * en rojo en alguna semana. Map artículo → descripciones de material en rojo.
   */
  conflictMap(editedKey: ItemKey): Map<ItemKey, string[]> {
    const out = new Map<ItemKey, string[]>()
    if (!editedKey || !this.leafReqByArticle.has(editedKey)) return out

    for (const { leafKey } of this.leavesOf(editedKey)) {
      if (!this.isBlocking(leafKey)) continue
      const saldo = this.saldoFin.get(leafKey)
      if (!saldo) continue
      let red = false
      for (let w = 0; w < this.numWeeks; w++) if (saldo[w] < -EPS) { red = true; break }
      if (!red) continue

      const matDesc = this.materialByKey.get(leafKey)?.descripcion ?? leafKey.split('|')[0]
      for (const { articleKey } of this.articlesByLeaf.get(leafKey) ?? []) {
        if (articleKey === editedKey) continue
        const list = out.get(articleKey)
        if (list) { if (!list.includes(matDesc)) list.push(matDesc) }
        else out.set(articleKey, [matDesc])
      }
    }
    return out
  }
}

// ── Helpers de presentación ──────────────────────────────────────────────────

/** Entero con separador de miles es-ES: 15000 → "15.000" */
export function fmtMiles(n: number): string {
  return Math.round(n).toLocaleString('es-ES', { maximumFractionDigits: 0 })
}

/** Agrupa artículos planificables por formato (me_ml), ordenado alfabéticamente. */
export function groupByFormato(articulos: PlanArticulo[]): { formato: string; articulos: PlanArticulo[] }[] {
  const map = new Map<string, PlanArticulo[]>()
  for (const a of articulos) {
    const arr = map.get(a.formato)
    if (arr) arr.push(a)
    else map.set(a.formato, [a])
  }
  return [...map.entries()]
    .sort((x, y) => x[0].localeCompare(y[0]))
    .map(([formato, arts]) => ({ formato, articulos: arts }))
}

export const SIN_FAMILIA = 'SIN FAMILIA'

/**
 * Agrupa por familia (me_inventfamily) y, dentro de cada familia, por formato.
 * Devuelve también la lista plana de artículos de la familia (para la fila de
 * familia). Familia vacía → SIN_FAMILIA. Todo ordenado alfabéticamente.
 */
export function groupByFamiliaFormato(
  articulos: PlanArticulo[],
): { familia: string; articulos: PlanArticulo[]; formatos: { formato: string; articulos: PlanArticulo[] }[] }[] {
  const map = new Map<string, PlanArticulo[]>()
  for (const a of articulos) {
    const fam = a.meInventfamily?.trim() || SIN_FAMILIA
    const arr = map.get(fam)
    if (arr) arr.push(a)
    else map.set(fam, [a])
  }
  return [...map.entries()]
    .sort((x, y) => x[0].localeCompare(y[0]))
    .map(([familia, arts]) => ({ familia, articulos: arts, formatos: groupByFormato(arts) }))
}
